package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.ProjectAccentKey
import dev.lingvo.shared.ProjectIconKey
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.ds.LProjectImageStyle
import dev.lingvo.site.ds.LProjectMark
import kotlinx.coroutines.CancellationException
import org.jetbrains.compose.web.dom.Img
import org.w3c.dom.url.URL

/**
 * A project's mark: its uploaded image if it has one, otherwise its preset icon.
 *
 * **Every surface that draws a project goes through here, and that is the point.** The upload
 * feature shipped with the image rendered in exactly one place — the preview inside the upload
 * control itself — so a reader could set a custom mark and then never see it: not in the project
 * list, not in the workspace switcher, not in the breadcrumb. Uploading a logo that appears only on
 * the page where you uploaded it is not the feature.
 *
 * **The image is fetched, not linked.** The endpoint is behind the bearer gate and the session
 * token lives in localStorage, so an `<img src>` pointing at it is answered 401 — which is exactly
 * what the first version did, on every render. Going through `ApiClient.projectIcon` puts the token
 * on the request; the bytes become an object URL.
 *
 * Object URLs are cached per slug and shared, because a project list draws twelve rows and each
 * would otherwise fetch and hold its own copy of the same image. The cache is the reason nothing
 * revokes on dispose: a URL revoked when one row leaves the screen would blank the same icon in
 * every other row still showing it. [forgetProjectIcon] is the deliberate release, called when an
 * icon is replaced or removed.
 */
@Composable
fun ProjectIcon(
    slug: String,
    name: String,
    iconKey: ProjectIconKey,
    accentKey: ProjectAccentKey,
    hasCustomIcon: Boolean,
    modifier: Modifier = Modifier,
) {
    var objectUrl by remember(slug) { mutableStateOf(projectIconUrls[slug]) }

    LaunchedEffect(slug, hasCustomIcon) {
        if (!hasCustomIcon) {
            objectUrl = null
            return@LaunchedEffect
        }
        projectIconUrls[slug]?.let {
            objectUrl = it
            return@LaunchedEffect
        }
        try {
            val blob = ApiClient.projectIcon(slug)
            val created = URL.createObjectURL(blob)
            projectIconUrls[slug] = created
            objectUrl = created
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (_: Throwable) {
            // The preset mark is a complete answer, so a failed fetch degrades to it rather than
            // to a broken image. A project's icon is not worth an error message.
            objectUrl = null
        }
    }

    val resolved = objectUrl
    if (hasCustomIcon && resolved != null) {
        Img(
            src = resolved,
            attrs = LProjectImageStyle.toModifier()
                .then(modifier)
                // Decorative: every place this renders puts the project's name beside it, and a
                // screen reader reading the name twice is worse than reading it once.
                .attr("alt", "")
                .attr("data-project-custom-icon", slug)
                .toAttrs(),
        )
    } else {
        LProjectMark(name, iconKey, accentKey, modifier)
    }
}

/**
 * Drops a cached object URL, so the next render fetches the replacement.
 *
 * Called after an upload or a removal. Without it the cache would keep serving the image from
 * before the change for the life of the page — the browser never re-reads an object URL.
 */
fun forgetProjectIcon(slug: String) {
    projectIconUrls.remove(slug)?.let { URL.revokeObjectURL(it) }
}

/**
 * Object URLs by project slug.
 *
 * A Compose state map so a fetch completing recomposes the rows waiting on it. Not revoked on
 * dispose — see the note on [ProjectIcon] about twelve rows sharing one URL.
 */
private val projectIconUrls = mutableStateMapOf<String, String>()
